// ════════════════════════════════════════════════════════════════════════════
//  Importação de funcionários do Ponto — leitura do arquivo e mapeamento de colunas.
//  Aqui só se lê o CSV e se decide qual coluna é qual campo; a normalização (CPF, PIS,
//  datas, telefone) e a validação acontecem no servidor (FuncionarioImportacaoService).
// ════════════════════════════════════════════════════════════════════════════

export type CampoImportacao =
  | "nome" | "cpf" | "matricula" | "pis" | "cargo" | "departamento" | "local"
  | "admissao" | "demissao" | "nascimento" | "rg" | "sexo" | "telefone" | "regime";

export interface LinhaImportacao {
  linha: number;
  nome?: string; cpf?: string; matricula?: string; pis?: string; cargo?: string;
  departamento?: string; local?: string; admissao?: string; demissao?: string;
  nascimento?: string; rg?: string; sexo?: string; telefone?: string; regime?: string;
}

export interface ArquivoLido {
  preset: string;
  linhas: LinhaImportacao[];
  /** Campos reconhecidos no arquivo (para mostrar ao usuário o que será importado). */
  campos: CampoImportacao[];
}

/** Lê o arquivo como UTF-8; se não for UTF-8 válido, cai para Windows-1252 (Excel BR). */
export async function lerTexto(arquivo: File): Promise<string> {
  const buf = await arquivo.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buf).replace(/^﻿/, "");
  } catch {
    return new TextDecoder("windows-1252").decode(buf);
  }
}

/** Separador mais frequente na 1ª linha (fora de aspas): ; , ou tab. */
function detectarSeparador(texto: string): string {
  const primeira = texto.split(/\r?\n/, 1)[0] ?? "";
  const cont: Record<string, number> = { ";": 0, ",": 0, "\t": 0 };
  let aspas = false;
  for (const c of primeira) {
    if (c === '"') aspas = !aspas;
    else if (!aspas && c in cont) cont[c]++;
  }
  const [sep, n] = Object.entries(cont).sort((a, b) => b[1] - a[1])[0];
  return n > 0 ? sep : ";";
}

/** CSV com aspas ("" = aspas literal) e quebra de linha dentro de aspas. */
export function parseCsv(texto: string): string[][] {
  const sep = detectarSeparador(texto);
  const linhas: string[][] = [];
  let campo = "", linha: string[] = [], aspas = false;
  for (let i = 0; i < texto.length; i++) {
    const c = texto[i];
    if (aspas) {
      if (c === '"' && texto[i + 1] === '"') { campo += '"'; i++; }
      else if (c === '"') aspas = false;
      else campo += c;
    } else if (c === '"') aspas = true;
    else if (c === sep) { linha.push(campo); campo = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && texto[i + 1] === "\n") i++;
      linha.push(campo); campo = "";
      if (linha.some((v) => v.trim() !== "")) linhas.push(linha);
      linha = [];
    } else campo += c;
  }
  linha.push(campo);
  if (linha.some((v) => v.trim() !== "")) linhas.push(linha);
  return linhas;
}

const chave = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** Cabeçalhos aceitos → campo. Cobre a exportação do sistema de ponto anterior e nomes comuns. */
const SINONIMOS: Record<string, CampoImportacao> = {
  nome: "nome", nomecompleto: "nome", funcionario: "nome", colaborador: "nome",
  cpf: "cpf", cpfdofuncionario: "cpf",
  matricula: "matricula", mat: "matricula", registro: "matricula",
  pis: "pis", nis: "pis", pispasep: "pis", pisnis: "pis",
  cargo: "cargo", funcao: "cargo",
  departamento: "departamento", setor: "departamento", depto: "departamento",
  localizacao: "local", local: "local", estabelecimento: "local", loja: "local",
  dataadmissao: "admissao", datadeadmissao: "admissao", admissao: "admissao", dtadmissao: "admissao",
  datademissao: "demissao", datadedemissao: "demissao", demissao: "demissao", desligamento: "demissao",
  datadesligamento: "demissao", datadedesligamento: "demissao",
  datanascimento: "nascimento", datadenascimento: "nascimento", nascimento: "nascimento", dtnascimento: "nascimento",
  rg: "rg", identidade: "rg",
  sexo: "sexo", genero: "sexo",
  telefone: "telefone", celular: "telefone", fone: "telefone",
  regimedetrabalho: "regime", regime: "regime",
};

/**
 * Lista de funcionários exportada SEM cabeçalho pelo sistema de ponto anterior (13 colunas):
 * matrícula, nome, PIS, cargo, departamento, CNPJ, admissão, demissão, —, localização, —, —,
 * e uma última coluna que NÃO é importada (parece senha/PIN do sistema antigo).
 */
const LISTA_SEM_CABECALHO: (CampoImportacao | null)[] = [
  "matricula", "nome", "pis", "cargo", "departamento", null, "admissao", "demissao", null, "local", null, null, null,
];

const CNPJ = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/;

export function interpretar(tabela: string[][]): ArquivoLido {
  if (tabela.length === 0) throw new Error("O arquivo está vazio.");

  const cab = tabela[0].map((c) => SINONIMOS[chave(c)] ?? null);
  const reconhecidos = cab.filter(Boolean) as CampoImportacao[];
  if (reconhecidos.includes("nome") && reconhecidos.length >= 2) {
    return {
      preset: "Planilha com cabeçalho (exportação completa de funcionários)",
      campos: [...new Set(reconhecidos)],
      linhas: tabela.slice(1).map((cols, i) => montar(cols, cab, i + 2)),
    };
  }

  const amostra = tabela[0];
  if (amostra.length === LISTA_SEM_CABECALHO.length && CNPJ.test((amostra[5] ?? "").trim())) {
    return {
      preset: "Lista simples sem cabeçalho (13 colunas, sem CPF)",
      campos: LISTA_SEM_CABECALHO.filter(Boolean) as CampoImportacao[],
      linhas: tabela.map((cols, i) => montar(cols, LISTA_SEM_CABECALHO, i + 1)),
    };
  }

  throw new Error(
    "Formato não reconhecido. Use uma planilha com cabeçalho contendo ao menos Nome, Matrícula e CPF.",
  );
}

function montar(cols: string[], mapa: (CampoImportacao | null)[], linha: number): LinhaImportacao {
  const r: LinhaImportacao = { linha };
  mapa.forEach((campo, i) => {
    if (campo && cols[i] != null && cols[i].trim() !== "" && r[campo] == null) r[campo] = cols[i].trim();
  });
  return r;
}

export const ROTULO_CAMPO: Record<CampoImportacao, string> = {
  nome: "Nome", cpf: "CPF", matricula: "Matrícula", pis: "PIS", cargo: "Cargo",
  departamento: "Departamento", local: "Local", admissao: "Admissão", demissao: "Demissão",
  nascimento: "Nascimento", rg: "RG", sexo: "Sexo", telefone: "Telefone", regime: "Regime",
};
